/**
 * Transmission icons + display helpers — shared across pages that render a vehicle.
 *
 * Single source of truth for the "vehicle transmission" UI vocabulary.
 * The backend always sends "MANUAL" or "AUTOMATIC" on the wire (legacy CVT/DCT
 * collapse to AUTOMATIC server-side via Vehicle.displayTransmission), but this
 * resolver is final-clamping defensively so any unexpected value buckets to
 * AUTOMATIC and never throws.
 *
 * Public surface:
 *   - transmissionDisplay(value)  → { bucket, label, short } | null
 *   - TRANSMISSION_OPTIONS        → list for form pickers (Manual / Automatic)
 *   - <ManualShifterIcon />       → H-pattern stick-shift glyph
 *   - <AutoCogIcon />             → cog glyph
 *   - <TransmissionIcon />        → bucket-aware icon picker
 *   - <TransmissionBadge />       → "icon + label" rendered in three variants:
 *                                   pill (default), inline, icon-only
 *
 * Used by:
 *   - BookingFormModal.jsx     (vehicle list pill, selector buttons)
 *   - BookingsPage.jsx         (week/day/month cards, booking detail panel)
 *   - JobsPage.jsx             (kanban + planner job cards)
 *   - JobDetailModal.jsx       (header subtitle, vehicle info box)
 */

import React from 'react';

// ─── Display resolver ──────────────────────────────────────────────────────
// Returns null when the input is genuinely missing (so callers can skip
// rendering). A non-null, non-MANUAL value collapses to AUTOMATIC, mirroring
// the backend's Vehicle.displayTransmission helper.
export const transmissionDisplay = (value) => {
    if (!value) return null;
    const v = String(value).toUpperCase();
    if (v === 'MANUAL') return { bucket: 'MANUAL', label: 'Manual', short: 'M' };
    return { bucket: 'AUTOMATIC', label: 'Automatic', short: 'A' };
};

// Form-picker list. UI vocabulary, not the full enum (CVT/DCT are not exposed).
export const TRANSMISSION_OPTIONS = [
    { value: 'MANUAL',    label: 'Manual'    },
    { value: 'AUTOMATIC', label: 'Automatic' },
];

// ─── Icons ─────────────────────────────────────────────────────────────────
// Same 24×24 viewBox + 2px stroke as lucide so they sit naturally next to
// the rest of the icon set.

// H-pattern gearbox layout with a knob — universal symbol for stick-shift.
export const ManualShifterIcon = ({ size = 14, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         className={className} aria-hidden="true">
        <line x1="6"  y1="8"   x2="6"  y2="20" />
        <line x1="18" y1="8"   x2="18" y2="20" />
        <line x1="6"  y1="14"  x2="18" y2="14" />
        <circle cx="12" cy="6" r="2.5" fill="currentColor" />
        <line x1="12" y1="8.5" x2="12" y2="14" />
    </svg>
);

// Cog — "the gearbox handles it for you".
export const AutoCogIcon = ({ size = 14, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         className={className} aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

// Bucket → icon component. Returns null for unknown buckets.
export const TransmissionIcon = ({ bucket, size = 14, className = '' }) => {
    if (bucket === 'MANUAL')    return <ManualShifterIcon size={size} className={className} />;
    if (bucket === 'AUTOMATIC') return <AutoCogIcon       size={size} className={className} />;
    return null;
};

// ─── Badge ─────────────────────────────────────────────────────────────────
// Renders nothing when value is missing/empty — drop-in safe for any context.
//
// variant:
//   - 'pill'   (default) coloured rounded pill, the BookingFormModal vehicle-list look
//                        (text-[10px] font-medium, px-1.5 py-0.5, amber/sky)
//   - 'inline'           bare coloured icon + small label, for tight card rows
//                        where a pill would fight other badges
//   - 'icon'             icon only, with a tooltip — densest sites (week/month grid)
//
// `size` overrides the icon size; otherwise it picks a sensible default per variant.
export const TransmissionBadge = ({
                                      value,
                                      variant = 'pill',
                                      size,
                                      className = '',
                                  }) => {
    const t = transmissionDisplay(value);
    if (!t) return null;

    const isManual = t.bucket === 'MANUAL';

    if (variant === 'icon') {
        return (
            <span
                title={`${t.label} transmission`}
                className={`inline-flex items-center shrink-0 ${
                    isManual ? 'text-amber-600' : 'text-sky-600'
                } ${className}`}
            >
                <TransmissionIcon bucket={t.bucket} size={size ?? 11} />
            </span>
        );
    }

    if (variant === 'inline') {
        return (
            <span
                title={`${t.label} transmission`}
                className={`inline-flex items-center gap-1 shrink-0 text-xs font-medium ${
                    isManual ? 'text-amber-700' : 'text-sky-700'
                } ${className}`}
            >
                <TransmissionIcon bucket={t.bucket} size={size ?? 12} />
                {t.label}
            </span>
        );
    }

    // pill (default) — exact match for the existing BookingFormModal look.
    return (
        <span
            title={`${t.label} transmission`}
            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                isManual ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'
            } ${className}`}
        >
            <TransmissionIcon bucket={t.bucket} size={size ?? 11} />
            {t.label}
        </span>
    );
};